/**
 * Component: Galen Search widget surface
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R10
 *
 * Embeddable Galen Search surface. Wraps
 * ``POST /api/v1/search/galen`` (hybrid search with optional
 * LLM summarisation). Provides a query input, the inline
 * Search button, the LLM summary (rendered as markdown to
 * match the chat surface), a result list, and the shared
 * inline error banner.
 *
 * Layout mirrors the Semantic Search surface: a "Galen
 * Search" heading + intro line, the query input with a
 * leading magnifier icon and the Search button inline on
 * its right, an empty-state before any search runs. The
 * Batch C representative "Source" filter was dropped —
 * the portal page doesn't carry it in this view either,
 * and the request continues to send ``filters.result_type``
 * fixed at ``all`` server-side semantics.
 *
 * Risk Links: R7, R11
 * FMEA Links: FM7, FM11
 */

import React, { useCallback, useState } from 'react';

import type { WidgetApiClient } from '../api/client';
import { WidgetError } from '../api/errors';
import { trackWidgetUsage } from '../core/usage-tracking';
import { BrowseLink } from './browse-link';
import { ChatMarkdown } from './chat-markdown';
import {
  SURFACE_PRIMARY_BUTTON_STYLE,
  SURFACE_RESULTS_LIST_STYLE,
  SURFACE_RESULT_CARD_STYLE,
  SURFACE_WRAPPER_STYLE,
  SearchResultExpander,
  SurfaceErrorBanner,
  explainWidgetError,
  getMetaString,
  normalizeQuotes,
  pickResultSubtitle,
  pickResultTitle,
} from './shared';
import { SearchFilterPanel } from './search-filter-panel';
import {
  type Facet,
  type SearchFilters,
  INITIAL_FILTERS,
  toFilterRequest,
} from './search-filter-types';
import { useFilterAutoSearch } from './use-filter-auto-search';

interface GalenSearchResult {
  document_id: string;
  title: string;
  content: string;
  score: number;
  ranking_position: number;
  semantic_score?: number | null;
  keyword_score?: number | null;
  captions?: string[];
  highlights?: Record<string, string[]>;
  metadata?: Record<string, unknown>;
  source_url?: string | null;
}

interface GalenSearchResponse {
  llm_response?: string | null;
  has_answer?: boolean;
  is_grounded?: boolean;
  results?: GalenSearchResult[];
  total_count?: number | null;
  query?: string;
  mode?: string;
  safety_decision?: string;
  safety_message?: string | null;
  /** WIDGET-V1-R27: available index facets for the refine panel. */
  facets?: Facet[];
}

export interface GalenSearchProps {
  client: WidgetApiClient;
  baseUrl: string;
  token: string;
  onError?: (error: Error) => void;
  enableBrowseLinks?: boolean;
}

type Phase = 'idle' | 'loading' | 'done' | 'error';

const GALEN_INPUT_AUTOFILL_CSS = [
  '[data-galen-surface="galen-search"] input:-webkit-autofill,',
  '[data-galen-surface="galen-search"] input:-webkit-autofill:hover,',
  '[data-galen-surface="galen-search"] input:-webkit-autofill:focus,',
  '[data-galen-surface="galen-search"] input:-webkit-autofill:active{',
  '-webkit-box-shadow:0 0 0 1000px var(--galen-background,#ffffff) inset!important;',
  'box-shadow:0 0 0 1000px var(--galen-background,#ffffff) inset!important;',
  '-webkit-text-fill-color:var(--galen-foreground,#0f172a)!important;',
  'caret-color:var(--galen-foreground,#0f172a);',
  'transition:background-color 9999s ease-in-out 0s;}',
].join('');

function SearchGlyph(): React.ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

export const GalenSearch: React.FC<GalenSearchProps> = ({
  client,
  onError,
  enableBrowseLinks = false,
}) => {
  const [query, setQuery] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [response, setResponse] = useState<GalenSearchResponse | null>(null);
  // WIDGET-V1-R27: server-side faceted filtering state.
  const [filters, setFilters] = useState<SearchFilters>(INITIAL_FILTERS);
  const [facets, setFacets] = useState<Facet[]>([]);
  // Bumped on a fresh query submit to collapse the refine panel.
  const [filterCollapseToken, setFilterCollapseToken] = useState(0);

  // WIDGET-V1-R27: re-runnable search. Keeps the galen-specific
  // `filters.result_type` and adds the top-level faceted refine params.
  const runSearch = useCallback(async () => {
    if (query.trim().length === 0) return;

    setPhase('loading');
    setErrorMessage(null);

    // WIDGET-V1-R25: fire search_query on submit.
    void trackWidgetUsage(client, 'galen-search', 'search_query', {
      query_text: query,
      mode: 'galen',
    });

    try {
      const body = await client.postJson<GalenSearchResponse>(
        '/api/v1/search/galen',
        {
          query: normalizeQuotes(query),
          filters: { result_type: 'all' },
          ...toFilterRequest(filters),
          prioritize_recent: filters.prioritizeRecent,
        },
      );
      setResponse(body);
      setFacets(body.facets ?? []);
      setPhase('done');
    } catch (e) {
      const err = e instanceof Error ? e : new WidgetError(String(e));
      setErrorMessage(explainWidgetError(err));
      setPhase('error');
      onError?.(err);
    }
  }, [client, query, filters, onError]);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (phase === 'loading') return;
      setFilterCollapseToken((n) => n + 1);
      void runSearch();
    },
    [phase, runSearch],
  );

  // WIDGET-V1-R27: debounced re-search when a refine selection changes.
  useFilterAutoSearch(filters, response !== null, () => void runSearch());

  const isBusy = phase === 'loading';
  const resultCount =
    typeof response?.total_count === 'number'
      ? response.total_count
      : (response?.results?.length ?? 0);

  return (
    <div data-galen-surface="galen-search" style={SURFACE_WRAPPER_STYLE}>
      <div data-galen-galen-search-header>
        <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700 }}>
          Galen Search
        </h1>
        <p
          style={{
            margin: '0.25rem 0 0',
            fontSize: '0.9rem',
            color: 'var(--galen-muted, #475569)',
          }}
        >
          Search ESC guidelines and publications with an AI-generated summary.
        </p>
      </div>

      {errorMessage !== null && <SurfaceErrorBanner message={errorMessage} />}

      <form
        onSubmit={(e) => void handleSubmit(e)}
        data-galen-galen-search-form
        style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}
      >
        <style>{GALEN_INPUT_AUTOFILL_CSS}</style>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            padding: '0 0.6rem',
            border: '1px solid var(--galen-border, #e2e8f0)',
            borderRadius: 'var(--galen-border-radius, 0.5rem)',
            background: 'var(--galen-background, #ffffff)',
            color: 'var(--galen-muted, #475569)',
          }}
        >
          <SearchGlyph />
          <input
            id="galen-search-query"
            data-galen-galen-search-query
            type="search"
            aria-label="Galen Search query"
            autoComplete="off"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={isBusy}
            placeholder="Search ESC guidelines and publications…"
            style={{
              flex: 1,
              minWidth: 0,
              boxSizing: 'border-box',
              padding: '0.5rem 0',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'var(--galen-foreground, #0f172a)',
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              appearance: 'none',
              WebkitAppearance: 'none',
            }}
          />
        </div>
        <button
          type="submit"
          disabled={isBusy || query.trim().length === 0}
          data-galen-galen-search-submit
          style={{
            ...SURFACE_PRIMARY_BUTTON_STYLE(isBusy || query.trim().length === 0),
            marginTop: 0,
            flexShrink: 0,
          }}
        >
          {isBusy ? 'Searching…' : 'Search'}
        </button>
      </form>

      {/* WIDGET-V1-R27: faceted refine panel (self-hides when nothing to refine) */}
      <SearchFilterPanel
        filters={filters}
        onFiltersChange={setFilters}
        onReset={() => setFilters(INITIAL_FILTERS)}
        facets={facets}
        resultCount={response !== null ? resultCount : undefined}
        collapseToken={filterCollapseToken}
      />

      {phase === 'idle' && (
        <div
          data-galen-galen-search-empty
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '2.5rem 1rem',
            textAlign: 'center',
            color: 'var(--galen-muted, #475569)',
          }}
        >
          <span style={{ opacity: 0.7 }}>
            <svg
              viewBox="0 0 24 24"
              width="36"
              height="36"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </span>
          <p style={{ margin: 0, fontSize: '1rem', fontWeight: 500, color: 'var(--galen-foreground, #0f172a)' }}>
            Enter a query to search
          </p>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            Galen Search combines keyword and semantic retrieval, then summarises the top results.
          </p>
        </div>
      )}

      {phase === 'done' && response && (
        <div data-galen-galen-search-results>
          {response.safety_decision &&
            response.safety_decision !== 'allow' &&
            response.safety_message && (
              <div
                data-galen-galen-search-safety
                style={{
                  padding: '0.75rem',
                  border: '1px solid var(--galen-border, #e2e8f0)',
                  borderRadius: 'var(--galen-border-radius, 0.5rem)',
                  background: 'var(--galen-background, #fffbeb)',
                  marginBottom: '0.75rem',
                }}
              >
                {response.safety_message}
              </div>
            )}
          {response.has_answer && response.llm_response && (
            <div
              data-galen-galen-search-summary
              style={{
                padding: '0.75rem',
                border: '1px solid var(--galen-border, #e2e8f0)',
                borderRadius: 'var(--galen-border-radius, 0.5rem)',
                background: 'var(--galen-background, #ffffff)',
                marginBottom: '0.75rem',
              }}
            >
              <ChatMarkdown content={response.llm_response} />
            </div>
          )}
          <ul style={SURFACE_RESULTS_LIST_STYLE}>
            {(response.results ?? []).map((result, index) => {
              const title = pickResultTitle(result);
              const subtitle = pickResultSubtitle(result);
              const source = getMetaString(result.metadata, 'source');
              const guidelineId =
                result.metadata?.['Guidelineid'] ??
                result.metadata?.['GuidelineId'] ??
                null;
              const guidelineSectionIdRaw =
                result.metadata?.['GuidelineSectionId'];
              const sectionId =
                guidelineSectionIdRaw != null &&
                guidelineSectionIdRaw !== 'null'
                  ? (guidelineSectionIdRaw as number | string)
                  : null;
              const guidelineName = getMetaString(
                result.metadata,
                'GuidelineName',
              );
              const sectionName = getMetaString(
                result.metadata,
                'GuidelineSectionName',
              );
              return (
                <li
                  key={result.document_id}
                  data-galen-search-result-card
                  style={SURFACE_RESULT_CARD_STYLE}
                >
                  <div style={{ fontWeight: 600 }}>
                    {result.source_url ? (
                      <a
                        href={result.source_url}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {title}
                      </a>
                    ) : (
                      title
                    )}
                  </div>
                  {subtitle && (
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--galen-muted, #475569)',
                        marginTop: '0.125rem',
                      }}
                    >
                      {subtitle}
                    </div>
                  )}
                  <SearchResultExpander
                    result={result}
                    surfaceKey="galen-search"
                    client={client}
                    query={query}
                    footerRight={
                      <BrowseLink
                        enabled={enableBrowseLinks}
                        guidelineId={guidelineId as number | string | null}
                        sectionId={sectionId}
                        client={client}
                        surfaceKey="galen-search"
                        documentId={result.document_id}
                        rankingPosition={index + 1}
                        guidelineName={guidelineName || undefined}
                        sectionName={sectionName || undefined}
                        query={query}
                      />
                    }
                  />
                  {source && (
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--galen-muted, #94a3b8)',
                        marginTop: '0.25rem',
                      }}
                    >
                      {source}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {(response.results ?? []).length === 0 &&
            !(response.has_answer && response.llm_response) && (
              <div
                style={{ fontSize: '0.9rem', color: 'var(--galen-muted, #475569)' }}
              >
                No results.
              </div>
            )}
        </div>
      )}
    </div>
  );
};
